import React, { useState } from 'react';
import { PageHeader, Tabs } from '../../../components/ds';
import { FiDatabase, FiLayers, FiGlobe, FiCpu, FiLayout, FiSettings, FiPlay, FiSliders } from 'react-icons/fi';
import HistoricalDataInstrumentList from './components/HistoricalDataInstrumentList';
import HistoricalDataTimeline from './components/HistoricalDataTimeline';
import RealTimeFeedsList from './components/RealTimeFeedsList';
import RealTimeSimulation from './components/RealTimeSimulation';
import { services } from '../../../services/api';

import MarketStatusDashboard from './components/MarketStatusDashboard';
import MDMSOrchestratorView from './components/MDMSOrchestratorView';

import FusionPriorityConfig from './components/FusionPriorityConfig';
import VolumeSettingsList from './components/VolumeSettingsList';

const MarketDataManagement = () => {
  const [activeTab, setActiveTab] = useState('status'); // 'status' | 'historical' | 'realtime' | 'orchestrator' | 'priority' | 'volume' | 'simulation'
  
  // Separate View States for Independent Navigation
  const [historicalView, setHistoricalView] = useState({ mode: 'list', instrument: null });
  const [realtimeView, setRealtimeView] = useState({ mode: 'list', instrument: null });
  
  // Simulation pre-fill data (when creating from timeline)
  const [simulationPrefill, setSimulationPrefill] = useState(null);

  // Handler for creating simulation from timeline view
  const handleCreateSimulation = (data) => {
    console.log('📊 Creating simulation from timeline:', data);
    setSimulationPrefill(data);
    setActiveTab('simulation');
  };

  // Historical Handlers
  const handleHistoricalSelect = async (instrument) => {
    try {
      // Fetch complete instrument details from Instrument Master
      // This includes broker selection fields: primary_broker_id, last_updated_by_broker_id, broker_data_sources
      const response = await services.instrumentMaster.getInstrument(instrument.instrument_id);
      const fullInstrumentData = response.data;
      
      // Merge MDMS display data with Instrument Master broker fields
      const enrichedInstrument = {
        ...instrument,  // Keep MDMS UI fields (total_records, status, etc.)
        ...fullInstrumentData,  // Add Instrument Master fields (primary_broker_id, etc.)
        // Ensure we preserve the display fields from MDMS
        total_records: instrument.total_records,
        first_data_time: instrument.first_data_time,
        last_data_time: instrument.last_data_time,
      };
      
      console.log('📋 Enriched instrument with broker fields:', {
        symbol: enrichedInstrument.symbol,
        primary_broker_id: enrichedInstrument.primary_broker_id,
        last_updated_by_broker_id: enrichedInstrument.last_updated_by_broker_id,
        broker_data_sources: enrichedInstrument.broker_data_sources
      });
      
      setHistoricalView({ mode: 'detail', instrument: enrichedInstrument });
    } catch (error) {
      console.error('Failed to fetch full instrument details:', error);
      // Fallback to using MDMS data only
      console.warn('⚠️ Using MDMS data only (broker fields may be missing)');
      setHistoricalView({ mode: 'detail', instrument });
    }
  };

  const handleHistoricalBack = () => {
    setHistoricalView({ mode: 'list', instrument: null });
  };

  // Realtime Handlers
  const handleRealtimeSelect = async (feed) => {
    try {
      // Fetch complete instrument details from Instrument Master
      // This includes the proper UUID instrument_id and broker fields
      const response = await services.instrumentMaster.getInstrument(feed.instrument_id);
      const fullInstrumentData = response.data;
      
      // Merge feed data with Instrument Master instrument fields
      const enrichedInstrument = {
        ...feed,  // Keep feed fields (capture_tick, capture_minute, etc.)
        ...fullInstrumentData,  // Add Instrument Master fields (UUID instrument_id, broker fields, etc.)
        // Ensure we preserve the display fields from feed
        is_continuous: feed.is_continuous,
        active_expiry_date: feed.active_expiry_date,
        next_rollover_date: feed.next_rollover_date,
      };
      
      console.log('📋 Enriched realtime feed with Instrument Master data:', {
        symbol: enrichedInstrument.symbol,
        instrument_id: enrichedInstrument.instrument_id,
        primary_broker_id: enrichedInstrument.primary_broker_id,
      });
      
      setRealtimeView({ mode: 'detail', instrument: enrichedInstrument });
    } catch (error) {
      console.error('Failed to fetch full instrument details:', error);
      // Fallback to using feed data only
      console.warn('⚠️ Using feed data only (Instrument Master fields may be missing)');
      setRealtimeView({ mode: 'detail', instrument: feed });
    }
  };

  const handleRealtimeBack = () => {
    setRealtimeView({ mode: 'list', instrument: null });
  };

  return (
    <div className="min-h-screen bg-main-bg dark:bg-main-dark-bg pt-0 px-4 md:px-6 pb-10 space-y-6">
      {/* Standardized page header (common across all pages) */}
      <PageHeader
        title="Market Data Center"
        subtitle="Manage historical archives, real-time data feeds, and monitor global market status."
      />

      {/* Main Tabs */}
      <Tabs
        className="mb-6"
        value={activeTab}
        onChange={setActiveTab}
        tabs={[
          { label: 'Market Status', value: 'status', icon: <FiGlobe /> },
          { label: 'Historical Data', value: 'historical', icon: <FiDatabase /> },
          { label: 'Real-time Feeds', value: 'realtime', icon: <FiLayers /> },
          { label: 'Orchestrator (Debug)', value: 'orchestrator', icon: <FiCpu /> },
          { label: 'Priority Config', value: 'priority', icon: <FiSettings /> },
          { label: 'Volume Settings', value: 'volume', icon: <FiSliders /> },
          { label: 'Simulation', value: 'simulation', icon: <FiPlay /> },
        ]}
      />

      {/* Content Area */}
      <div className="min-h-[600px]">
        {/* Historical Tab Content */}
        {activeTab === 'historical' && (
           <>
              {historicalView.mode === 'list' ? (
                  <HistoricalDataInstrumentList onSelectInstrument={handleHistoricalSelect} />
              ) : (
                  <HistoricalDataTimeline 
                      instrument={historicalView.instrument} 
                      onBack={handleHistoricalBack}
                      onCreateSimulation={handleCreateSimulation}
                  />
              )}
           </>
        )}
        
        {/* Real-time Tab Content */}
        {activeTab === 'realtime' && (
            <>
               {realtimeView.mode === 'list' ? (
                   <RealTimeFeedsList onSelectInstrument={handleRealtimeSelect} />
               ) : (
                   <HistoricalDataTimeline
                       instrument={realtimeView.instrument}
                       onBack={handleRealtimeBack}
                       onCreateSimulation={handleCreateSimulation}
                   />
               )}
            </>
        )}

        {/* Volume Settings Tab Content */}
        {activeTab === 'volume' && (
            <VolumeSettingsList />
        )}

        {/* Market Status Tab Content */}
        {activeTab === 'status' && (
            <MarketStatusDashboard />
        )}

        {/* Orchestrator Tab Content */}
        {activeTab === 'orchestrator' && (
            <MDMSOrchestratorView />
        )}



        {/* Priority Config Tab Content */}
        {activeTab === 'priority' && (
            <FusionPriorityConfig />
        )}

        {/* Simulation Tab Content */}
        {activeTab === 'simulation' && (
            <RealTimeSimulation 
                prefillData={simulationPrefill}
                onPrefillConsumed={() => setSimulationPrefill(null)}
            />
        )}
      </div>
    </div>
  );
};

export default MarketDataManagement;
